import { Link } from 'lucide-react';
import { useState, useCallback } from 'react';

import { LinkResult } from './LinkResult';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { useCreateLink } from '@/hooks/useLinks';
import { useSecretTokenStore } from '@/stores/secretToken';
import type { ExpirationPresetType, CreateLinkResponse } from '@/types/api';
import { EXPIRATION_PRESET_OPTIONS } from '@/types/api';

export function LinkForm(): React.ReactElement {
  const [url, setUrl] = useState('');
  const [expiresPreset, setExpiresPreset] =
    useState<ExpirationPresetType>('unlimited');
  const [result, setResult] = useState<CreateLinkResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isFirstLink, setIsFirstLink] = useState(false);

  const { secretToken } = useSecretTokenStore();

  const { mutate, isPending } = useCreateLink({
    onSuccess: (data) => {
      setResult(data);
      setError(null);
      // First link if no secretToken was stored before this request
      setIsFirstLink(!secretToken && !data.existingLink);
    },
    onError: (err) => {
      setError(err.message);
      setResult(null);
    },
  });

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();

      if (!url.trim()) {
        setError('URLを入力してください');
        return;
      }

      // Basic URL validation
      try {
        new URL(url);
      } catch {
        setError('有効なURLを入力してください');
        return;
      }

      setError(null);
      mutate({
        originalUrl: url,
        expiresPreset:
          expiresPreset === 'unlimited' ? undefined : expiresPreset,
      });
    },
    [url, expiresPreset, mutate]
  );

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* URL Input */}
      <div className="space-y-2">
        <label htmlFor="url" className="text-sm font-medium">
          短縮したいURL
        </label>
        <Input
          id="url"
          type="url"
          placeholder="https://example.com/very/long/path"
          value={url}
          onChange={(e) => {
            setUrl(e.target.value);
          }}
          disabled={isPending}
        />
      </div>

      {/* Expiration Preset */}
      <div className="space-y-2">
        <label htmlFor="expires" className="text-sm font-medium">
          有効期限
        </label>
        <Select
          id="expires"
          options={EXPIRATION_PRESET_OPTIONS}
          value={expiresPreset}
          onChange={(e) => {
            setExpiresPreset(e.target.value as ExpirationPresetType);
          }}
          disabled={isPending}
        />
      </div>

      {/* Error Message */}
      {error && (
        <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
          {error}
        </div>
      )}

      {/* Submit Button */}
      <Button type="submit" disabled={isPending} className="w-full">
        {isPending ? (
          '生成中...'
        ) : (
          <>
            <Link className="h-4 w-4" />
            <span>短縮URLを生成</span>
          </>
        )}
      </Button>

      {/* Result */}
      {result && <LinkResult result={result} isFirstLink={isFirstLink} />}
    </form>
  );
}
